import React, {forwardRef, useImperativeHandle} from 'react';
import {Checkbox, Col, HorizontalRule, Row, TextLink} from '@snsw-gel/react';
import config from '../../config/config';
import useTrConfig from '../../hooks/useTrConfig';
import {InputValueAndError} from '../../types/InputValueAndError';
import {FormErrorAnchor, StateSetter} from '../../utils/helpers';
import TollReliefConfig from '../../config/TollReliefConfig';
import {FeatureToggleName, useBaseFeature} from '../../hooks/features/useBaseFeature';

export interface RegistrationFlowTermsAndConditionsProps {
    isSoleTraderRegistration: boolean;
    setAcceptNotifications: StateSetter<InputValueAndError<boolean>>;
    acceptNotifications: InputValueAndError<boolean>;
    setAgreeToTr2PersonalTerms?: StateSetter<InputValueAndError<boolean>>;
    agreeToTr2PersonalTerms?: InputValueAndError<boolean>;
    setAgreeToTr3PersonalTerms?: StateSetter<InputValueAndError<boolean>>;
    agreeToTr3PersonalTerms?: InputValueAndError<boolean>;
    setAgreeToTr2SoleTraderTerms?: StateSetter<InputValueAndError<boolean>>;
    agreeToTr2SoleTraderTerms?: InputValueAndError<boolean>;
}

export const tAndCFormInputDefs = (
    trConfig: any,
    agreeToTr2PersonalTerms?: InputValueAndError<any>,
    agreeToTr3PersonalTerms?: InputValueAndError<any>,
    agreeToTr3SoleTraderTerms?: InputValueAndError<any>,
): FormErrorAnchor[] => {
    const termsAndConditionsCheckboxDefs: FormErrorAnchor[] = [];
    // TODO remove after tr2 decom
    if (agreeToTr2PersonalTerms) {
        termsAndConditionsCheckboxDefs.push(
            {
                inputValue: agreeToTr2PersonalTerms,
                anchor: TollReliefConfig.anchors.agreeToTr2PersonalTerms,
                message: 'Read and accept the terms and conditions for the 40% rebate scheme',
            }
        );
    }

    if (agreeToTr3PersonalTerms) {
        termsAndConditionsCheckboxDefs.push(
            {
                inputValue: agreeToTr3PersonalTerms,
                anchor: TollReliefConfig.anchors.agreeToTr3PersonalTerms,
                message: `Read and accept the terms and conditions for the ${trConfig.getTR3weeklyCap()} weekly cap rebate scheme`,
            }
        );
    }
    if (agreeToTr3SoleTraderTerms) {
        termsAndConditionsCheckboxDefs.push(
            {
                inputValue: agreeToTr3SoleTraderTerms,
                anchor: TollReliefConfig.anchors.agreeToTr2SoleTraderTerms,
                message: 'Read and accept the terms and conditions to continue',
            }
        );
    }
    return termsAndConditionsCheckboxDefs;
}

export type RegistrationFlowTermsAndConditionsHandle = {
    validate: () => boolean;
};

const RegistrationFlowTermsAndConditions =
    forwardRef<RegistrationFlowTermsAndConditionsHandle, RegistrationFlowTermsAndConditionsProps>(
        (props, ref) => {

    const trConfig = useTrConfig();
    // this to allow parent page to call validation of terms and conditions. Parent component should pass in useRef ref in props
    useImperativeHandle(ref, () => ({
        validate() {
            return isValidData();
        }
    }));

    const { isFeatureEnabled } = useBaseFeature();
    const isTr2Decommissioned = isFeatureEnabled(FeatureToggleName.TR2_DECOMMISSION)

    const {isSoleTraderRegistration,
    acceptNotifications,
    setAcceptNotifications,
    agreeToTr2PersonalTerms,
    setAgreeToTr2PersonalTerms,
    agreeToTr3PersonalTerms,
    setAgreeToTr3PersonalTerms,
    agreeToTr2SoleTraderTerms,
    setAgreeToTr2SoleTraderTerms,
    } = props;

    const onChangeAcceptNotifications = (v: boolean ) => {
        setAcceptNotifications(acceptNotifications.withValue(v || false));
    }

    const onChangeAcceptSoleTraderTerms = (value: boolean ) => {
        // @ts-ignore will not be null
        setAgreeToTr2SoleTraderTerms(
            // @ts-ignore will not be null
            agreeToTr2SoleTraderTerms.withValue(value || false).validated()
        );
    }

    const onChangeAcceptPersonalTr2Terms = (value: boolean )=> {
        // @ts-ignore will not be null
        setAgreeToTr2PersonalTerms(
            // @ts-ignore will not be null
            agreeToTr2PersonalTerms.withValue(value || false).validated()
        );
    }
    const onChangeAcceptPersonalTr3Terms = (value: boolean ) => {
        // @ts-ignore will not be null
        setAgreeToTr3PersonalTerms(
            // @ts-ignore will not be null
            agreeToTr3PersonalTerms.withValue(value || false).validated()
        );
    }

    const isValidData = () => {
        let valid = true;
        if (agreeToTr2PersonalTerms && setAgreeToTr2PersonalTerms &&
            agreeToTr2PersonalTerms.validated().hasError) {
            valid = false;
            setAgreeToTr2PersonalTerms(agreeToTr2PersonalTerms.withError());
        }

        if (agreeToTr3PersonalTerms && setAgreeToTr3PersonalTerms &&
            agreeToTr3PersonalTerms.validated().hasError) {
            setAgreeToTr3PersonalTerms(agreeToTr3PersonalTerms.withError());
            valid = false;
        }

        if (agreeToTr2SoleTraderTerms && setAgreeToTr2SoleTraderTerms &&
            agreeToTr2SoleTraderTerms.validated().hasError) {
            setAgreeToTr2SoleTraderTerms(agreeToTr2SoleTraderTerms.withError());
            valid = false;
        }
        return valid;
    }

    return (
        <>
            <Row>
                <Col span={8}>
                    <HorizontalRule
                        marginTop={ '3.5rem' }
                        marginBottom={ '3.5rem' }
                    />
                </Col>
            </Row>
            {/* TODO remove block after Tr2 decommission */}
            {isSoleTraderRegistration &&
                <>
                    <Row>
                        <Col span={8}>
                            <p>
                                Please read the{' '}
                                <TextLink isExternal href={config.links.tr2TermsAndConditionsBusinessLink} id='tr2TermsAndConditionsBusinessLink'>
                                    Terms and Conditions
                                </TextLink>
                                {' ' } and the{' ' }
                                <TextLink isExternal href={config.links.privacyCollectionNoticeLink} id='privacyCollectionNoticeLink'>
                                    Privacy collection notice
                                </TextLink>
                            </p>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={8}>
                            <Checkbox
                                margin={{top:'none'}}
                                key={'tr2AcceptSoleTraderTandC'}
                                disabled={false}
                                errorMessage='Read and accept the terms and conditions to continue.'
                                hasError={agreeToTr2SoleTraderTerms?.hasError}
                                id={TollReliefConfig.anchors.agreeToTr2SoleTraderTerms}
                                label={'I have read and accept the Terms and conditions.'}
                                name='tr2AcceptSoleTraderTandC'
                                onChange={(value) => {
                                    onChangeAcceptSoleTraderTerms(value === 'true');
                                }}
                                checked={agreeToTr2SoleTraderTerms?.value === true}
                                value={'true'}
                                isRequired
                            />
                        </Col>
                    </Row>
                </>
            }
            {/* TODO remove block after Tr2 decommission */}
            {!isSoleTraderRegistration && !isTr2Decommissioned &&
                <>
                    <Row>
                        <Col span={8}>
                            <p>
                                Please read the{' '}
                                <TextLink
                                    href={config.links.privacyCollectionNoticeLink} id='privacyCollectionNoticeLink'
                                    target='_blank'
                                    rel='noreferrer noopener'
                                >
                                    privacy collection notice.
                                </TextLink>
                            </p>
                            <p>
                                Please read the{' '}
                                <TextLink
                                    href={config.links.tr2TermsAndConditionsPersonalLink} id='tr2TermsAndConditionsPersonalLink'
                                    target='_blank'
                                    rel='noreferrer noopener'
                                >
                                    Terms and Conditions
                                </TextLink>
                                {' '}for the 40% rebate scheme.
                            </p>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={8}>
                            <Checkbox
                                margin={{top:'none'}}
                                key={'tr2AcceptTandC'}
                                disabled={false}
                                errorMessage='Read and accept the terms and conditions to continue.'
                                hasError={agreeToTr2PersonalTerms?.hasError}
                                id={TollReliefConfig.anchors.agreeToTr2PersonalTerms}
                                label={'I have read and accept the Terms and conditions for the 40% rebate scheme.'}
                                name='tr2AcceptTandC'
                                onChange={(value) => {
                                    onChangeAcceptPersonalTr2Terms(value === 'true')
                                }}
                                checked={agreeToTr2PersonalTerms?.value === true}
                                value={'true'}
                                isRequired
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col span={8}>
                            <p>
                                Please read the{' '}
                                <TextLink
                                    href={config.links.termsAndConditionsLink} id='termsAndConditionsLink'
                                    target='_blank'
                                    rel='noreferrer noopener'
                                > Terms and Conditions
                                </TextLink>
                                {' '}for the {trConfig.getTR3weeklyCap()} weekly cap rebate scheme.
                            </p>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={8}>
                            {/* If you change the label, change what the pdf uses in TollReliefRegistration.json */}
                            <Checkbox
                                margin={{top:'none'}}
                                key={'tr3AcceptTandC'}
                                disabled={false}
                                errorMessage='Read and accept the terms and conditions to continue.'
                                hasError={agreeToTr3PersonalTerms?.hasError}
                                id={TollReliefConfig.anchors.agreeToTr3PersonalTerms}
                                label={`I have read and accept the Terms and conditions for the ${trConfig.getTR3weeklyCap()} weekly cap rebate scheme.`}
                                name='tr3AcceptTandC'
                                onChange={(value) => {
                                    onChangeAcceptPersonalTr3Terms(value === 'true')
                                }}
                                checked={agreeToTr3PersonalTerms?.value === true}
                                value={'true'}
                                isRequired
                            />
                        </Col>
                    </Row>
                </>
            }
            {!isSoleTraderRegistration && isTr2Decommissioned &&
                <>
                    <br/>
                    <Row>
                        <Col span={8}>
                            Read the{' '}
                            <TextLink isExternal href={config.links.termsAndConditionsLink} id='termsAndConditionsLink'>
                                Terms and conditions.
                            </TextLink>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={8}>
                            <p>
                                For more information about how we collect, use and disclose your personal information,
                                including who we may disclose it to, go to our {' '}
                                <TextLink target='_blank' href={config.links.privacyCollectionNoticeLink}
                                          id='privacyCollectionNoticeLink'>
                                    Privacy Collection Notice.
                                </TextLink>
                            </p>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={8}>
                            {/* If you change the label, change what the pdf uses in TollReliefRegistration.json */}
                            <Checkbox
                                margin={{top: 'none'}}
                                key={'tr3AcceptTandC'}
                                disabled={false}
                                errorMessage='Read and accept the terms and conditions to continue.'
                                hasError={agreeToTr3PersonalTerms?.hasError}
                                id={TollReliefConfig.anchors.agreeToTr3PersonalTerms}
                                label={'I have read and accept the Terms and conditions.'}
                                name='tr3AcceptTandC'
                                onChange={(value) => {
                                    onChangeAcceptPersonalTr3Terms(value === 'true')
                                }}
                                checked={agreeToTr3PersonalTerms?.value === true}
                                value={'true'}
                                isRequired
                            />
                        </Col>
                    </Row>
                </>
            }
            <br/>
            <Row>
                <Col span={8}>
                    <Checkbox
                        margin={{top:'none'}}
                        key={'acceptNotifications'}
                        disabled={false}
                        errorMessage=''
                        hasError={false}
                        id='acceptNotifications'
                        label={'I agree to receive notifications about Toll Relief including when I can apply for rebates.'}
                        name='acceptNotifications'
                        onChange={(value) => {
                            onChangeAcceptNotifications(value === 'true')
                        }}
                        checked={acceptNotifications.value === true}
                        value={'true'}
                        isOptional
                    />
                </Col>
            </Row>
        </>
    )
})

export default RegistrationFlowTermsAndConditions
